import React, { useState, useMemo, useEffect } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import styled from 'styled-components';

const StyledMap = styled(Map)`
  margin-left: -10px;
  margin-right: -10px;
`;

const MapPin = styled.div`
  svg {
    width: 60px;
    height: 60px;
    fill: #DB4437;
    filter: drop-shadow(0 2px 2px rgba(0,0,0,0.3));
  }
`;

const mapContainerStyle = {
  height: "60vh",
  width: "100%"
};

const isValidLatLng = (lat, lng) => {
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

const checkGoogleAPIs = () => {
  try {
    // Log the API key (first few characters)
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    console.log('API Key loaded:', apiKey ? '✓' : '✗');
    if (!apiKey) {
      console.log('Missing Google Maps API Key');
    }

    // Check if Google Maps is loaded
    if (!window.google?.maps) {
      console.log('❌ Google Maps not loaded');
      return;
    }

    // Log available libraries
    console.log('Google Maps Libraries Status:', {
      maps: '✓',
      places: window.google.maps.places ? '✓' : '✗',
      geometry: window.google.maps.geometry ? '✓' : '✗',
      geocoding: window.google.maps.Geocoder ? '✓' : '✗'
    });

  } catch (error) {
    console.error('Error checking Google Maps status:', error);
  }
};

function MapContent({ defaultCenter, markerPosition, setMarkerPosition, setAddress }) {
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const inputRef = React.useRef(null);
  const autocompleteRef = React.useRef(null); // Track autocomplete instance
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (!placesLib || !inputRef.current || autocompleteRef.current) return;

    try {     
      // Create the autocomplete instance
      autocompleteRef.current = new placesLib.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'au' },
        fields: [
          'address_components',
          'formatted_address',
          'geometry',
          'name',
          'place_id'
        ],
        types: ['geocode']
      });

      // Add the place_changed listener
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();

        if (!place.geometry?.location) {
          console.log('No geometry for place:', place);
          return;
        }

        const newPosition = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        setMarkerPosition(newPosition);
        setAddress(place.formatted_address || place.name);
        
        if (map) {
          map.panTo(newPosition);
          map.setZoom(15);
        }
      });

      setIsLoading(false);

    } catch (error) {
      console.error('Error initializing Places:', error);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [placesLib, map, setMarkerPosition, setAddress, defaultCenter]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
      <div className="search-input-container">
        <input
          ref={inputRef}
          type="text"
          placeholder={isLoading ? "Loading..." : "Search for a location in Australia"}
          style={{ 
            width: '100%', 
            padding: '8px', 
            fontSize: '16px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            outline: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          disabled={isLoading}
          autoComplete="off"
        />
      </div>
      <div className="map-container">
        <p className="text-center mb-0">Move the red marker to the delivery location, then click Set Location.</p>
        <Map
          key={`${defaultCenter.lat}-${defaultCenter.lng}`}
          mapId={import.meta.env.VITE_GOOGLE_MAP_ID}
          style={mapContainerStyle}
          defaultCenter={defaultCenter}
          defaultZoom={15}
          onClick={(e) => {
            const newPosition = {
              lat: e.detail.latLng.lat,
              lng: e.detail.latLng.lng,
            };
            setMarkerPosition(newPosition);
          }}
          options={{
            streetViewControl: false,  // Disable street view
            // mapTypeControl: false,
            // fullscreenControl: false,
          }}
        >
          <AdvancedMarker
            position={markerPosition}
            draggable
            onDragEnd={(e) => {
              const newPosition = {
                lat: e.latLng.lat(),
                lng: e.latLng.lng(),
              };
              setMarkerPosition(newPosition);
              
            }}
          >
            <MapPin>
              <svg viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </MapPin>
          </AdvancedMarker>
        </Map>
      </div>
    </div>
  );
}

// Replace roundToSix with this helper
const roundToSix = (num) => {
  return Number(Number(num).toFixed(6));
};

// Main component
export default function MapWithAddress({ defaultCenter, lat, lng, handleSetLatLng }) {
  const [markerPosition, setMarkerPosition] = useState(defaultCenter);
  const [address, setAddress] = useState("");

  // Update markerPosition when defaultCenter changes
  useEffect(() => {
    setMarkerPosition(defaultCenter);
  }, [defaultCenter]);

    // Add this effect to update markerPosition when props change
    useEffect(() => {
      if (lat && lng && isValidLatLng(lat, lng)) {
        setMarkerPosition({
          lat: roundToSix(lat),
          lng: roundToSix(lng)
        });
      }
    }, [lat, lng]);

  // Compare using toFixed(6) strings for exact matching
  const positionMatchesMap = useMemo(() => {
    if (!markerPosition && !lat && !lng) return true;
    const currentLat = roundToSix(markerPosition.lat);
    const currentLng = roundToSix(markerPosition.lng);
    const propLat = roundToSix(lat);
    const propLng = roundToSix(lng);
    return currentLat === propLat && currentLng === propLng;
  }, [lat, lng, markerPosition]);

  return (
    <APIProvider
      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
      libraries={['places', 'marker']}
      region="AU"
    >
      <MapContent 
        defaultCenter={defaultCenter}
        markerPosition={markerPosition}
        setMarkerPosition={setMarkerPosition}
        setAddress={setAddress}
      />
      <div>
        <label className="text-sml text-center text-light">Marker Position: Latitude: {roundToSix(markerPosition.lat)} | Longitude: {roundToSix(markerPosition.lng)}</label>
      </div>
      
      <button
        className={`my-2 w-100 btn ${ positionMatchesMap ? 'btn-secondary' : 'btn-primary' }`}
        onClick={() => handleSetLatLng(markerPosition.lat, markerPosition.lng)}
        disabled={positionMatchesMap}
      >
        Set Location
      </button>

    </APIProvider>
  );
}